import { DiagramComponent } from '../../core/DiagramComponent';
import type { BinConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

/**
 * Bin Component - A container that displays a specified number of dots inside
 * 
 * Properties:
 * - position: [x, y] - Center position of the bin
 * - count: number - Number of dots to display (0-9)
 * - color: string - Color of the bin border and dots
 * - width: number - Width of the bin (default: 2.5)
 * - height: number - Height of the bin (default: 2.5)
 */
class Bin extends DiagramComponent<BinConfig> {
  constructor(config: BinConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): JXGElement[] {
    const x = Number(this.evaluateExpression(this.config.position[0]));
    const y = Number(this.evaluateExpression(this.config.position[1]));
    const count = Number(this.evaluateExpression(this.config.count || 0));
    const color = this.config.color || '#000000';
    const width = this.config.width || 2.5;
    const height = this.config.height || 2.5;

    // Don't render anything if count is 0
    if (count <= 0) {
      return this.elements;
    }

    // Create bin rectangle (container)
    const left = x - width / 2;
    const right = x + width / 2;
    const top = y + height / 2;
    const bottom = y - height / 2;

    // Create the four corners of the rectangle
    const p1 = this.board.create('point', [left, top], {
      visible: false,
      withLabel: false,
      name: ''
    });
    const p2 = this.board.create('point', [right, top], {
      visible: false,
      withLabel: false,
      name: ''
    });
    const p3 = this.board.create('point', [right, bottom], {
      visible: false,
      withLabel: false,
      name: ''
    });
    const p4 = this.board.create('point', [left, bottom], {
      visible: false,
      withLabel: false,
      name: ''
    });

    // Create the rectangle polygon
    const rectangle = this.board.create('polygon', [p1, p2, p3, p4], {
      strokeColor: color,
      strokeWidth: 3,
      fillColor: 'none',
      vertices: {
        visible: false,
        withLabel: false,
        name: ''
      }
    });

    this.elements.push(p1, p2, p3, p4, rectangle);

    // Create dots inside the bin based on count
    // Arrange dots in a grid pattern (up to 3x3)
    if (count > 0 && count <= 9) {
      const cols = Math.min(3, count);
      const rows = Math.ceil(count / 3);
      
      const dotSpacingX = width / (cols + 1);
      const dotSpacingY = height / (rows + 1);
      
      let dotIndex = 0;
      for (let row = 0; row < rows && dotIndex < count; row++) {
        const dotsInRow = Math.min(3, count - dotIndex);
        for (let col = 0; col < dotsInRow; col++) {
          const dotX = left + (col + 1) * dotSpacingX;
          const dotY = top - (row + 1) * dotSpacingY;
          
          const dot = this.board.create('point', [dotX, dotY], {
            size: 4,
            fillColor: color,
            strokeColor: color,
            fixed: true,
            withLabel: false,
            name: ''
          });
          
          this.elements.push(dot);
          dotIndex++;
        }
      }
    }

    return this.elements;
  }

  update(variables: Variables): void {
    // Merge new variables into parentState (don't replace entirely)
    this.parentState = { ...this.parentState, ...variables };
    
    // Remove old elements
    this.elements.forEach(el => {
      if (el && this.board.objectsList && this.board.objectsList.includes(el)) {
        this.board.removeObject(el);
      }
    });
    this.elements = [];

    // Recreate with new values
    this.render();
  }
}

export default Bin;
